import { useEffect, useMemo, useRef, useState } from "react";
import { Paperclip, X } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { Dialog } from "../../components/Dialog";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";
import { CategoryManager } from "./CategoryManager";
import type {
  Expense,
  ExpenseCategory,
  ExpenseCategoryOption,
  ExpenseKind,
  ExpenseKindOption,
  QuotePick,
} from "../../api/types";
import { MoneyField } from "../../components/MoneyInput";

// 登錄 / 修改一筆雜支(第 16 輪,第 24 輪長出專案、憑證與附件)。
//
// **分類與憑證的中文字樣由後端給**(`/expense-categories`、`/expense-kinds`)——
// 前端自己再維護一份,兩份遲早會漂開,而漂開的那天畫面上會出現英文 FREIGHT。
//
// ==========================================================================
// 第 24 輪(業主 2026-09-21)
// ==========================================================================
// > 目前雜支是針對報價單 但是有時候可能幫忙買了什麼東西 要申請雜支請款
// > 不一定是哪個專案 …… 在最上面第一行是個別雜支的選項
// > 登入雜支 輸入時缺了附件(可有可無) 要上傳發票 收據 等等電子檔 但不是必填
// > 填寫的金額就是含稅價 稅務系統直接算百分之五就可以了
//
// **稅額不出現在這張表單上。** 金額是含稅價,內含的 5% 由後端算
// (`routers/expenses.py::tax_inside`)—— 但收據與無憑證沒有那個數字,
// 所以憑證種類是必要的一格,不是裝飾。
//
// --------------------------------------------------------------------------
// 為什麼不用 react-query
// --------------------------------------------------------------------------
// 這個對話框會被 `ProjectCost` 用,而 `ProjectCost` 掛在報價單編輯器裡 ——
// 編輯器那棵樹的測試沒有 `QueryClientProvider`(`AttachmentPanel` 也是為了
// 同一個理由用 `useEffect` + `api`)。跟著既有的慣例,好過逼十一個測試檔
// 都去包一層它們本來不需要的東西。

/** 下拉一次載幾張單。**一頁看得完的量** —— 還沒封存的單通常就這個量級。 */
const QUOTE_LIMIT = 100;

export interface ExpenseDialogProps {
  open: boolean;
  /** 預設要歸到哪一張單。從報價單上開的就帶著它,從雜支頁開的留空。 */
  quoteId?: number | null;
  /** 有值就是修改,沒有就是新增。 */
  expense?: Expense;
  onClose: () => void;
  onDone: () => void;
}

/** 讀一份清單,讀不到就當成空的 —— 一格下拉空著,其餘照樣填得完。
 *
 * **形狀也要擋。** `<Dialog>` 的 children 是 JSX,**即使 `open` 是 false
 * 也會先被求值** —— 拿到不是陣列的東西直接 `.map()`,會在這個對話框
 * 根本還沒打開的時候就把整棵樹炸掉(報價單編輯器跟著變白畫面)。
 */
function useList<T>(path: string, params?: Record<string, string | number>, version = 0): T[] {
  const [rows, setRows] = useState<T[]>([]);
  const key = JSON.stringify(params ?? {});
  useEffect(() => {
    let alive = true;
    api
      .get<unknown>(path, params)
      .then((got) => {
        if (!alive) return;
        const items = Array.isArray(got)
          ? got
          : Array.isArray((got as { items?: unknown })?.items)
            ? (got as { items: T[] }).items
            : [];
        setRows(items as T[]);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, version]);
  return rows;
}

export function ExpenseDialog({ open, quoteId, expense, onClose, onDone }: ExpenseDialogProps) {
  const toast = useToast();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // 分類可以在這個視窗裡自己增減(業主 2026-09-30);增減完 `version` 加一就重讀一次。
  const [categoryVersion, setCategoryVersion] = useState(0);
  const [managing, setManaging] = useState(false);
  const categories = useList<ExpenseCategoryOption>("/expense-categories", undefined, categoryVersion);
  const kinds = useList<ExpenseKindOption>("/expense-kinds");
  // **只列還沒封存的**(業主 2026-09-21 選的):封存過的單累積起來會把下拉
  // 撐成幾百行,而請款掛的多半是還在跑的案子。列表端點預設就排除已封存。
  const quotes = useList<QuotePick>("/quotes", { size: QUOTE_LIMIT });

  const [quote, setQuote] = useState<string>(
    String(expense?.quote_id ?? quoteId ?? ""),
  );
  const [category, setCategory] = useState<ExpenseCategory>(expense?.category ?? "TRANSPORT");
  const [kind, setKind] = useState<ExpenseKind>(expense?.kind ?? "RECEIPT");
  const [spentOn, setSpentOn] = useState(expense?.spent_on ?? today);
  const [amount, setAmount] = useState(expense?.amount ?? "");
  const [vendor, setVendor] = useState(expense?.vendor ?? "");
  const [vendorTaxId, setVendorTaxId] = useState(expense?.vendor_tax_id ?? "");
  // 以前用過的廠商(業主 2026-09-30,財務整理 5):打店家名稱時列出來,選了統編一起帶入。
  const vendors = useList<{ vendor: string; vendor_tax_id: string }>(
    "/expenses/vendors",
    { q: vendor.trim() },
  );
  const pickVendor = (value: string) => {
    setVendor(value);
    const known = vendors.find((one) => one.vendor === value);
    if (known && known.vendor_tax_id) setVendorTaxId(known.vendor_tax_id);
  };
  const [invoiceNo, setInvoiceNo] = useState(expense?.invoice_no ?? "");
  const [note, setNote] = useState(expense?.note ?? "");

  // 憑證的電子檔。**先上傳拿編號,存檔時才帶著它** —— 與報價單附件同一套。
  const [fileId, setFileId] = useState<number | null>(expense?.file_id ?? null);
  const [fileName, setFileName] = useState(expense?.file_name ?? "");
  const [uploading, setUploading] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  const [saving, setSaving] = useState(false);

  /** 從報價單頁開的對話框不給改歸屬 —— 那個入口本來就是「這張單的成本」。 */
  const locked = quoteId != null && !expense;

  const attach = async (file: File) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const got = await api.upload<{ file_id: number; original_name: string }>(
        "/expenses/attachments",
        form,
      );
      setFileId(got.file_id);
      setFileName(got.original_name);
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "檔案上傳失敗", "error");
    } finally {
      setUploading(false);
      if (picker.current) picker.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        // **空字串是「個別雜支」,要送 null** —— 送 0 或 "" 的話後端會當成
        // 一張 id 為 0 的單,而那張單不存在。
        quote_id: quote === "" ? null : Number(quote),
        category,
        kind,
        spent_on: spentOn,
        amount,
        vendor,
        vendor_tax_id: vendorTaxId.trim(),
        invoice_no: invoiceNo,
        note,
        file_id: fileId,
      };
      if (expense) {
        await api.put<Expense>(`/expenses/${expense.id}`, body);
      } else {
        await api.post<Expense>("/expenses", body);
      }
      toast.push(expense ? "已更新" : "已登錄", "success");
      onDone();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "存不起來", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={expense ? "修改雜支" : "登錄雜支"}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            loading={saving}
            /* 金額與日期是唯一兩個非有不可的 —— 其餘(含發票號碼與附件)都可空。 */
            disabled={!amount.trim() || !spentOn}
            onClick={() => void save()}
          >
            {expense ? "確定更新" : "確定登錄"}
          </Button>
        </div>
      }
    >
      {/* **第一行是「個別雜支」**(業主 2026-09-21)。幫忙買東西的請款不一定
          屬於任何專案,但它照樣是公司真的花出去的錢 —— 進報表,只是不進任何
          一個案子的毛利。 */}
      <Select
        label="歸屬專案"
        value={quote}
        disabled={locked}
        hint={
          locked
            ? "從這張報價單上登錄的,歸屬不會變"
            : "不屬於任何專案就留著第一行 —— 它照樣進公司的帳"
        }
        onChange={(e) => setQuote(e.target.value)}
        options={[
          { value: "", label: "個別雜支(不掛專案)" },
          ...quotes.map((one) => ({
            value: String(one.id),
            label: `${one.quote_no} ${one.title}`.trim(),
          })),
        ]}
      />

      {/* 字樣由後端給 —— 見檔頭。 */}
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Select
            label="分類"
            value={category}
            onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
            options={categories.map((one) => ({ value: one.value, label: one.label }))}
          />
        </div>
        <Button size="sm" variant="ghost" aria-expanded={managing} onClick={() => setManaging((v) => !v)}>
          管理分類
        </Button>
      </div>
      {managing ? (
        <CategoryManager
          categories={categories}
          onChanged={(created) => {
            setCategoryVersion((v) => v + 1);
            if (created) setCategory(created.value);
          }}
        />
      ) : null}

      {/* **憑證種類決定稅額。** 收據與無憑證沒有進項稅可以扣抵,硬算一個 5%
          拿去申報是補稅加罰的錯 —— 所以這一格不是裝飾。 */}
      <Select
        label="憑證種類"
        value={kind}
        hint="收據與無憑證沒有營業稅可以扣抵"
        onChange={(e) => setKind(e.target.value as ExpenseKind)}
        options={kinds.map((one) => ({ value: one.value, label: one.label }))}
      />

      <Input label="花費日" type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} />
      <MoneyField
        label="金額(含稅)"
        hint="就打發票或收據上的總額 —— 裡面的 5% 營業稅由系統自己算"
        value={amount}
        onValueChange={setAmount}
      />
      <Input
        label="店家 / 廠商"
        list="expense-vendor-options"
        autoComplete="off"
        value={vendor}
        onChange={(e) => pickVendor(e.target.value)}
      />
      <datalist id="expense-vendor-options">
        {vendors.map((one) => (
          <option key={`${one.vendor}-${one.vendor_tax_id}`} value={one.vendor}>
            {one.vendor_tax_id}
          </option>
        ))}
      </datalist>
      <Input
        label="賣方統編"
        hint="選填;選了以前用過的店家會自己帶入"
        inputMode="numeric"
        maxLength={8}
        value={vendorTaxId}
        onChange={(e) => setVendorTaxId(e.target.value)}
      />
      <Input
        label="發票號碼"
        hint="收據沒有號碼就留空"
        value={invoiceNo}
        onChange={(e) => setInvoiceNo(e.target.value)}
      />
      <Input label="說明" value={note} onChange={(e) => setNote(e.target.value)} />

      {/* 憑證的電子檔。**可有可無**(業主原話),所以它排在最後,
          而且沒有紅色星號、沒有任何會擋住存檔的東西。 */}
      <div className="flex flex-col gap-1">
        <span className="text-sm text-[var(--text-2)]">憑證電子檔(選填)</span>
        <input
          ref={picker}
          type="file"
          aria-label="憑證電子檔"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void attach(file);
          }}
        />
        {fileName ? (
          <div className="flex items-center gap-2 text-sm text-[var(--text)]">
            <Paperclip aria-hidden="true" size={15} className="shrink-0 text-[var(--text-3)]" />
            <span className="min-w-0 truncate">{fileName}</span>
            <button
              type="button"
              aria-label="移除附件"
              onClick={() => {
                setFileId(null);
                setFileName("");
              }}
              className="rounded-[var(--radius)] p-1 text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
            >
              <X aria-hidden="true" size={15} />
            </button>
          </div>
        ) : (
          <Button
            type="button"
            loading={uploading}
            icon={<Paperclip aria-hidden="true" size={15} />}
            onClick={() => picker.current?.click()}
          >
            選一個檔(發票、收據的照片或 PDF)
          </Button>
        )}
      </div>
    </Dialog>
  );
}
